export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error'

export interface LogContext {
  component?: string
  function?: string
  userId?: string
  sessionId?: string
  [key: string]: unknown
}

export interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  data?: unknown
  context?: LogContext
}

class Logger {
  private logLevel: LogLevel = 'debug'
  private isProduction: boolean = false

  constructor() {
    // Detectar ambiente usando las variables de Vite
    this.isProduction = import.meta.env.PROD || import.meta.env.NODE_ENV === 'production'
    this.logLevel = this.isProduction ? 'warn' : 'debug'
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['trace', 'debug', 'info', 'warn', 'error']
    return levels.indexOf(level) >= levels.indexOf(this.logLevel)
  }

  private formatMessage(level: LogLevel, message: string, _data?: unknown, context?: LogContext): string {
    const timestamp = new Date().toISOString()
    const contextStr = context ? ` [${Object.entries(context).map(([k, v]) => `${k}=${v}`).join(', ')}]` : ''
    return `[${timestamp}] ${level.toUpperCase()}${contextStr}: ${message}`
  }

  private logToConsole(level: LogLevel, message: string, data?: unknown, context?: LogContext) {
    if (!this.shouldLog(level)) return

    const formattedMessage = this.formatMessage(level, message, data, context)

      switch (level) {
      case 'error':
        console.error(formattedMessage, data)
          break
      case 'warn':
        console.warn(formattedMessage, data)
          break
      case 'info':
        console.info(formattedMessage, data)
          break
      case 'debug':
        console.debug(formattedMessage, data)
          break
      case 'trace':
        console.trace(formattedMessage, data)
          break
      }
    }

  private async logToRemote(entry: LogEntry) {
    // En producción, solo enviamos errores y warnings para no sobrecargar
    if (this.isProduction && (entry.level === 'error' || entry.level === 'warn')) {
      try {
        // Aquí podrías integrar con un servicio de logging externo
        // Por ejemplo: Sentry, LogRocket, DataDog, etc.
        // await externalLoggingService.send(entry)
        
        // Por ahora, silenciamos el logging en producción para evitar logs innecesarios
        // Solo mantenemos los logs de consola para errores críticos
      } catch (error) {
        // Si falla el logging remoto, no hacer nada para evitar loops
        console.error('Failed to send log to remote service:', error)
      }
    }
  }

  error(message: string, data?: unknown, context?: LogContext) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'error',
      message,
      data,
      context
    }
    
    this.logToConsole('error', message, data, context)
    this.logToRemote(entry)
  }

  warn(message: string, data?: unknown, context?: LogContext) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'warn',
      message,
      data,
      context
    }
    
    this.logToConsole('warn', message, data, context)
    this.logToRemote(entry)
  }

  info(message: string, data?: unknown, context?: LogContext) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'info',
      message,
      data,
      context
    }
    
    this.logToConsole('info', message, data, context)
    this.logToRemote(entry)
  }

  debug(message: string, data?: unknown, context?: LogContext) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'debug',
      message,
      data,
      context
    }
    
    this.logToConsole('debug', message, data, context)
    this.logToRemote(entry)
  }

  trace(message: string, data?: unknown, context?: LogContext) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'trace',
      message,
      data,
      context
    }
    
    this.logToConsole('trace', message, data, context)
    this.logToRemote(entry)
  }

  // Convenience method for component logging
  component(componentName: string) {
    return {
      error: (message: string, data?: unknown, additionalContext?: LogContext) => 
        this.error(message, data, { component: componentName, ...additionalContext }),
      warn: (message: string, data?: unknown, additionalContext?: LogContext) => 
        this.warn(message, data, { component: componentName, ...additionalContext }),
      info: (message: string, data?: unknown, additionalContext?: LogContext) => 
        this.info(message, data, { component: componentName, ...additionalContext }),
      debug: (message: string, data?: unknown, additionalContext?: LogContext) => 
        this.debug(message, data, { component: componentName, ...additionalContext }),
      trace: (message: string, data?: unknown, additionalContext?: LogContext) => 
        this.trace(message, data, { component: componentName, ...additionalContext })
    }
  }

  // Method to flush remaining logs (useful for cleanup)
  async flush() {
    // Could implement log flushing here if needed
  }
}

// Create singleton instance
export const logger = new Logger()

// Export convenience functions
export const log = {
  error: (message: string, data?: unknown, context?: LogContext) => logger.error(message, data, context),
  warn: (message: string, data?: unknown, context?: LogContext) => logger.warn(message, data, context),
  info: (message: string, data?: unknown, context?: LogContext) => logger.info(message, data, context),
  debug: (message: string, data?: unknown, context?: LogContext) => logger.debug(message, data, context),
  trace: (message: string, data?: unknown, context?: LogContext) => logger.trace(message, data, context),
  component: (componentName: string) => logger.component(componentName)
}

export default logger